/**
 * PostsTable — denser tabular view of iHerb posts.
 *
 * Mirrors the card view's selection + primary actions (Approve/Reject/Send/Delete).
 * Feedback editing and Refine intentionally live in the card view only — those need
 * inline form UI that doesn't fit a table row well.
 *
 * Uses TanStack Table (headless) on top of the project's existing Table primitives.
 * Selection state stays parent-managed (`selectedIds: Set<string>`) so it can be
 * consumed by `BulkActionBar` without duplicating state.
 */

import { useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Badge, Button, Table, TBody, Td, Th, THead, Tr } from '@lib/ui';
import type { IherbPost, PostStatus } from '../types';

function ShortUrlLink({ url }: { url: string }) {
  // Strip scheme so the cell stays compact; full URL on hover.
  const display = url.replace(/^https?:\/\//, '');
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block max-w-[22ch] truncate text-xs text-primary-600 hover:underline dark:text-primary-400"
      title={url}
    >
      {display}
    </a>
  );
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const STATUS_LABELS: Record<PostStatus, string> = {
  queued: 'Queued',
  scraping: 'Scraping…',
  generating: 'Generating…',
  refining: 'Refining…',
  pending: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  sent: 'Sent',
};

const STATUS_COLORS: Record<PostStatus, 'neutral' | 'info' | 'warning' | 'success' | 'error'> = {
  queued: 'neutral',
  scraping: 'info',
  generating: 'info',
  refining: 'info',
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  sent: 'neutral',
};

// Order used when sorting by status — in-progress first, then review-needed, then terminal states.
const STATUS_ORDER: Record<PostStatus, number> = {
  queued: 0,
  scraping: 1,
  generating: 2,
  refining: 3,
  pending: 4,
  approved: 5,
  rejected: 6,
  sent: 7,
};

function parsePrice(price: string | null): number {
  if (!price) return Number.POSITIVE_INFINITY; // missing prices sort last
  const digits = price.replace(/[^\d.]/g, '');
  const value = Number.parseFloat(digits);
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

export interface PostsTableProps {
  posts: IherbPost[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string, feedback: string) => void;
  onSend: (id: string) => void;
  onDelete: (id: string) => void;
  isUpdating: boolean;
  isSending: boolean;
}

const columnHelper = createColumnHelper<IherbPost>();

function buildColumns(handlers: {
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string, feedback: string) => void;
  onSend: (id: string) => void;
  onDelete: (id: string) => void;
  isUpdating: boolean;
  isSending: boolean;
}) {
  return [
    columnHelper.display({
      id: 'select',
      size: 40,
      header: () => null,
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={handlers.selectedIds.has(row.original.id)}
          onChange={() => handlers.onToggleSelect(row.original.id)}
          className="size-4 cursor-pointer accent-primary-600"
          aria-label="Select post"
        />
      ),
    }),
    columnHelper.accessor((row) => STATUS_ORDER[row.status], {
      id: 'status',
      header: 'Status',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge color={STATUS_COLORS[row.original.status]} size="sm">
          {STATUS_LABELS[row.original.status]}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: 'image',
      size: 80,
      header: 'Image',
      cell: ({ row }) =>
        row.original.image_url ? (
          <img
            src={`${API_BASE}${row.original.image_url}`}
            alt="Product"
            className="size-14 object-contain rounded"
            loading="lazy"
          />
        ) : null,
    }),
    columnHelper.accessor('message', {
      header: 'Message',
      enableSorting: false,
      cell: ({ row }) => (
        <>
          {row.original.message ? (
            <p
              dir="rtl"
              lang="he"
              className="text-sm text-gray-800 dark:text-dark-100 whitespace-pre-wrap leading-relaxed max-w-md"
            >
              {row.original.message}
            </p>
          ) : (
            <span className="text-sm italic text-gray-400 dark:text-dark-400">—</span>
          )}
          {row.original.feedback && (
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300 italic">
              Feedback: {row.original.feedback}
            </p>
          )}
        </>
      ),
    }),
    columnHelper.accessor((row) => parsePrice(row.price), {
      id: 'price',
      header: 'Price',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 dark:text-dark-200 font-medium">
          {row.original.price ?? '—'}
        </span>
      ),
    }),
    columnHelper.accessor('click_count', {
      id: 'click_count',
      header: 'Clicks',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.click_count > 0 ? (
          <span className="text-sm font-medium text-gray-700 dark:text-dark-200">
            {row.original.click_count}
          </span>
        ) : (
          <span className="text-sm text-gray-400 dark:text-dark-400">—</span>
        ),
    }),
    columnHelper.accessor('product_url', {
      header: 'Product',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.product_url ? (
          <a
            href={row.original.product_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block max-w-[18ch] truncate text-xs text-primary-600 hover:underline dark:text-primary-400"
            title={row.original.product_url}
          >
            {row.original.product_url.replace(/^https:\/\/il\.iherb\.com\/pr\//, '')}
          </a>
        ) : null,
    }),
    columnHelper.accessor('short_url', {
      id: 'short_url',
      header: 'Short URL',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.short_url ? (
          <ShortUrlLink url={row.original.short_url} />
        ) : (
          <span className="text-xs text-gray-400 dark:text-dark-400">—</span>
        ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const post = row.original;
        const isReviewable =
          post.status === 'pending' || post.status === 'approved' || post.status === 'rejected';
        const isSendable = post.status === 'approved';
        return (
          <div className="flex flex-wrap gap-1">
            {isReviewable && post.status !== 'approved' && (
              <Button
                color="success"
                size="xs"
                onClick={() => handlers.onApprove(post.id)}
                disabled={handlers.isUpdating}
              >
                Approve
              </Button>
            )}
            {isSendable && (
              <Button
                color="primary"
                size="xs"
                onClick={() => handlers.onSend(post.id)}
                disabled={handlers.isSending}
              >
                {handlers.isSending ? '…' : 'Send'}
              </Button>
            )}
            {isReviewable && post.status !== 'rejected' && (
              <Button
                color="error"
                variant="soft"
                size="xs"
                onClick={() => handlers.onReject(post.id, '')}
                disabled={handlers.isUpdating}
              >
                Reject
              </Button>
            )}
            <Button
              color="error"
              variant="flat"
              size="xs"
              onClick={() => {
                if (window.confirm('Delete this post?')) handlers.onDelete(post.id);
              }}
            >
              Delete
            </Button>
          </div>
        );
      },
    }),
  ];
}

export function PostsTable({
  posts,
  selectedIds,
  onToggleSelect,
  onApprove,
  onReject,
  onSend,
  onDelete,
  isUpdating,
  isSending,
}: PostsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: posts,
    columns: buildColumns({
      selectedIds,
      onToggleSelect,
      onApprove,
      onReject,
      onSend,
      onDelete,
      isUpdating,
      isSending,
    }),
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-x-auto">
      <Table hoverable className="w-full">
        <THead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();
                return (
                  <Th
                    key={header.id}
                    style={
                      header.column.columnDef.size
                        ? { width: header.column.columnDef.size }
                        : undefined
                    }
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span className="text-xs text-gray-400">
                          {sortDir === 'asc' ? '↑' : sortDir === 'desc' ? '↓' : '↕'}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </THead>
        <TBody>
          {table.getRowModel().rows.map((row) => (
            <Tr
              key={row.id}
              className={
                selectedIds.has(row.original.id)
                  ? 'bg-primary-50 dark:bg-primary-950/30'
                  : undefined
              }
            >
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
              ))}
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
